import React from "react";
import type { CSSProperties } from "react";
import { buildSanityImageSrc } from "~/features/sanity/media/image-url";

/** Inline styles for mux-player, including `--*` CSS variables (not on older @mux/mux-player-react .d.ts). */
export type MuxCSSProperties = CSSProperties & {
  [key: `--${string}`]: string | undefined;
};
export function optimizeMuxImageUrl(url: string, maxWidth: number): string {
  if (!url || !url.includes("image.mux.com")) {
    return url;
  }
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  const existing = params.get("width");
  const parsed = existing ? parseInt(existing, 10) : NaN;
  const width = Number.isFinite(parsed) ? Math.min(parsed, maxWidth) : maxWidth;
  params.set("width", String(Math.max(1, width)));
  if (!params.has("fit_mode")) {
    params.set("fit_mode", "smartcrop");
  }
  const queryString = params.toString();
  const origin = base ?? url;
  return queryString ? `${origin}?${queryString}` : origin;
}
export function buildPosterSrc(src?: string, maxWidth: number = 1200) {
  if (!src) {
    return;
  }
  if (src.includes("image.mux.com")) {
    return optimizeMuxImageUrl(src, maxWidth);
  }
  return buildSanityImageSrc(src, {
    w: maxWidth,
    q: 40
  });
}

/**
 * Patches a mux-player instance to address Lighthouse a11y findings on its
 * shadow-DOM error dialog (`mxp-dialog`):
 *   - aria-dialog-name (no accessible name on role=dialog)
 *   - button-name (the dialog's `.close` button has no accessible name)
 *
 * The dialog lives in the player's shadow DOM, lacks a `part` attribute, and
 * receives no slotted content from us — so document-level CSS can't reach it.
 * We inject a stylesheet that hides the dialog (background videos can't surface
 * a meaningful error UI anyway, and the poster acts as the fallback) and patch
 * any rendered instance with explicit aria attributes as a defensive fallback.
 */
export function useMuxPlayerA11yPatch(el: unknown) {
  React.useEffect(() => {
    const element = el as (HTMLElement & {
      shadowRoot?: ShadowRoot | null;
    }) | null;
    if (!element?.shadowRoot) return;
    const style = document.createElement("style");
    style.textContent = `mxp-dialog { display: none !important; }`;
    element.shadowRoot.appendChild(style);
    const patchDialog = (root: ParentNode) => {
      const dialog = root.querySelector("mxp-dialog");
      if (dialog && !dialog.getAttribute("aria-label")) {
        dialog.setAttribute("aria-label", "Video player error");
      }
      const close = dialog?.shadowRoot?.querySelector("button.close");
      if (close && !close.getAttribute("aria-label")) {
        close.setAttribute("aria-label", "Close");
      }
    };
    patchDialog(element.shadowRoot);
    const observer = new MutationObserver(() => patchDialog(element.shadowRoot!));
    observer.observe(element.shadowRoot, {
      childList: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      style.remove();
    };
  }, [el]);
}